import {LitElement, html, nothing, css} from 'lit';
import {customElement, state, query} from 'lit/decorators.js';
import {ClamMainStyles} from './clam-main-styles.js';
import {Loader, type LoaderOptions} from 'google-maps';

type Page =
    | 'nosotros'
    | 'organigrama'
    | 'medico'
    | 'ambiental'
    | 'seguridad'
    | 'capacitacion'
    | 'conocelos'
    | 'registro'
    | 'noticias'
    | 'eventos'
    | 'vinculacion'
    | '404';

const PAGE_MODULES: Record<string, () => Promise<unknown>> = {
    nosotros: () => import('./views/clam-nosotros.js'),
    organigrama: () => import('./views/clam-organigrama.js'),
    medico: () => import('./views/clam-subcomite.js'),
    ambiental: () => import('./views/clam-subcomite.js'),
    seguridad: () => import('./views/clam-subcomite.js'),
    capacitacion: () => import('./views/clam-subcomite.js'),
    conocelos: () => import('./views/clam-conocelos.js'),
    registro: () => import('./views/clam-registro.js'),
    noticias: () => import('./views/clam-noticias.js'),
    eventos: () => import('./views/clam-eventos.js'),
    vinculacion: () => import('./views/clam-vinculacion.js'),
};

const routeProgressStyles = css`
    .route-progress {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        height: 3px;
        z-index: 10000;
        overflow: hidden;
        pointer-events: none;
    }
    .route-progress-bar {
        position: absolute;
        top: 0;
        left: 0;
        height: 100%;
        width: 30%;
        background: var(--clam-primary, #5a9d1e);
        border-radius: 0 3px 3px 0;
        box-shadow: 0 0 8px rgba(90, 157, 30, 0.5);
        animation: clam-routebar 1s ease-in-out infinite;
    }
    @keyframes clam-routebar {
        0% {
            left: -30%;
        }
        50% {
            left: 50%;
        }
        100% {
            left: 100%;
        }
    }
`;

@customElement('clam-main-app')
export class ClamMainApp extends LitElement {
    static styles = [ClamMainStyles, routeProgressStyles];

    @state() private _page: Page = 'nosotros';
    @state() private _navigating = false;
    @state() private _mobile = false;
    @state() private _drawerOpen = false;
    @state() private _condensed = false;
    @state() private _openGroup: string | null = null;
    @query('.app-header') private _header!: HTMLElement;
    private _mq?: MediaQueryList;

    connectedCallback(): void {
        super.connectedCallback();
        this._onLocation();
        window.addEventListener('popstate', this._onLocation);
        window.addEventListener('scroll', this._onScroll, {passive: true});
        document.addEventListener('click', this._onDocClick);
        this._mq = window.matchMedia('(max-width: 1024px)');
        this._mobile = this._mq.matches;
        this._mq.addEventListener('change', (e) => (this._mobile = e.matches));
    }

    disconnectedCallback(): void {
        super.disconnectedCallback();
        window.removeEventListener('popstate', this._onLocation);
        window.removeEventListener('scroll', this._onScroll);
        document.removeEventListener('click', this._onDocClick);
    }

    private _onDocClick = (e: MouseEvent): void => {
        const target = e.target as Node;
        if (!this._openGroup) return;
        const group = this.renderRoot.querySelector(`.nav-group[data-key="${this._openGroup}"]`);
        if (group && !group.contains(target)) this._openGroup = null;
    };

    private _onLocation = (): void => {
        const raw = window.location.pathname.replace(/^\//, '');
        const p = raw as Page;
        this._page = (PAGE_MODULES[p] ? p : raw === '' ? 'nosotros' : '404') as Page;
        void this._navigate(this._page);
    };

    private _onScroll = (): void => {
        const condensed = window.scrollY > 150;
        if (condensed !== this._condensed) this._condensed = condensed;
    };

    private async _navigate(page: Page): Promise<void> {
        this._navigating = true;
        const loader = PAGE_MODULES[page];
        if (loader) {
            try {
                await loader();
            } catch {
                this._page = '404';
            }
        }
        // dejar que la nueva vista pinte antes de ocultar la barra
        requestAnimationFrame(() =>
            requestAnimationFrame(() => setTimeout(() => (this._navigating = false), 150)),
        );
    }

    firstUpdated(): void {
        this._startGoogleMap();
        this.dispatchEvent(new CustomEvent('content-loaded', {bubbles: true}));
    }

    private async _startGoogleMap(): Promise<void> {
        try {
            const loader = new Loader('AIzaSyDnpRN7YuYzFo6Tzs5aeU6qs-pmK1EHTX8', {} as LoaderOptions);
            const google = await loader.load();
            const mapEl = this.renderRoot.querySelector('#map') as HTMLDivElement;
            if (!mapEl) return;
            const map = new google.maps.Map(mapEl, {
                zoom: 15,
                scrollwheel: false,
                center: {lat: 19.1747787, lng: -96.2296098},
            });
            const marker = new google.maps.Marker({
                position: {lat: 19.1730494, lng: -96.2304793},
                map,
            });
            const infowindow = new google.maps.InfoWindow({
                content: '<div style="text-align:center;"><p>Fortalecemos Tú Seguridad</p></div>',
            });
            marker.addListener('click', () => infowindow.open(map, marker));
        } catch (e) {
            console.warn('google maps failed', e);
        }
    }

    private _go(page: string): void {
        window.history.pushState({}, '', '/' + page);
        this._page = page as Page;
        void this._navigate(page as Page);
        this._drawerOpen = false;
        this._returnTop();
    }

    private _returnTop(): void {
        document.body.scrollTop = 0;
        document.documentElement.scrollTop = 0;
    }

    private _renderPage(): unknown {
        switch (this._page) {
            case 'nosotros':
                return html`<clam-nosotros></clam-nosotros>`;
            case 'organigrama':
                return html`<clam-organigrama></clam-organigrama>`;
            case 'medico':
                return html`<clam-subcomite subcomite="medico"></clam-subcomite>`;
            case 'ambiental':
                return html`<clam-subcomite subcomite="ambiental"></clam-subcomite>`;
            case 'seguridad':
                return html`<clam-subcomite subcomite="seguridad"></clam-subcomite>`;
            case 'capacitacion':
                return html`<clam-subcomite subcomite="capacitacion"></clam-subcomite>`;
            case 'conocelos':
                return html`<clam-conocelos></clam-conocelos>`;
            case 'registro':
                return html`<clam-registro></clam-registro>`;
            case 'noticias':
                return html`<clam-noticias></clam-noticias>`;
            case 'eventos':
                return html`<clam-eventos></clam-eventos>`;
            case 'vinculacion':
                return html`<clam-vinculacion></clam-vinculacion>`;
            default:
                return html`<clam-404></clam-404>`;
        }
    }

    private _navItem(label: string, page: string) {
        const active = this._page === page;
        return html`<a
            class=${active ? 'active' : nothing}
            href="/${page}"
            aria-current=${active ? 'page' : nothing}
            @click=${(e: Event) => this._navClick(e, page)}
            >${label}</a
        >`;
    }

    private _navClick(e: Event, page: string): void {
        e.preventDefault();
        this._openGroup = null;
        this._go(page);
    }

    private _group(label: string, key: string, items: Array<[string, string]>) {
        const open = this._openGroup === key;
        const containsActive = items.some(([, p]) => p === this._page);
        return html`
            <div class="nav-group ${open ? 'open' : ''}" data-key=${key}>
                <button
                    type="button"
                    class="nav-trigger ${containsActive ? 'contains-active' : ''}"
                    aria-expanded=${open}
                    @click=${(e: Event) => {
                        e.stopPropagation();
                        this._openGroup = open ? null : key;
                    }}
                >
                    ${label}<span class="caret">▾</span>
                </button>
                ${open
                    ? html`
                          <div class="nav-group-items">
                              ${items.map(([l, p]) => this._navItem(l, p))}
                          </div>
                      `
                    : null}
            </div>
        `;
    }

    private _upItem(href: string, icon: string, text: unknown, external = false) {
        return html`
            <a
                class="up-toolbar-content"
                href=${href}
                target=${external ? '_blank' : nothing}
                rel=${external ? 'noopener' : nothing}
            >
                <img src=${icon} alt="" />
                <p>${text}</p>
            </a>
        `;
    }

    render(): unknown {
        return html`
            ${this._navigating
                ? html`<div class="route-progress" aria-hidden="true"><div class="route-progress-bar"></div></div>`
                : nothing}
            ${this._mobile && this._drawerOpen
                ? html`
                      <div class="drawer-scrim" @click=${() => (this._drawerOpen = false)}></div>
                      <div class="mobile-drawer">
                          <div class="drawer-header">
                              <img src="/img/branding/logo_navbar.png" />
                              <button @click=${() => (this._drawerOpen = false)}>✕</button>
                          </div>
                          <div class="drawer-nav">
                              ${this._navItem('Nosotros', 'nosotros')}
                              ${this._group('Subcomités', 'sub', [
                                  ['Médico', 'medico'],
                                  ['Medio Ambiente', 'ambiental'],
                                  ['Seguridad', 'seguridad'],
                                  ['Capacitación', 'capacitacion'],
                              ])}
                              ${this._group('Asociados', 'aso', [
                                  ['Conócelos', 'conocelos'],
                                  ['Registro', 'registro'],
                              ])}
                              ${this._group('Novedades', 'nov', [
                                  ['Noticias', 'noticias'],
                                  ['Eventos', 'eventos'],
                              ])}
                              ${this._navItem('Vinculación', 'vinculacion')}
                              <a class="drawer-socio-btn" href="/login" @click=${() => (this._drawerOpen = false)}>Socio CLAM</a>
                          </div>
                      </div>
                  `
                : nothing}

            <header class="app-header ${this._condensed ? 'condensed' : ''}">
                <div id="up-toolbar">
                    ${this._upItem(
                        'mailto:clamveracruz@hotmail.com?subject=Solicito Información CLAM Web',
                        '/icons/email.png',
                        'clamveracruz@hotmail.com',
                    )}
                    ${this._upItem(
                        'https://wa.me/522299038985?text=Deseo%20más%20información%20del%20CLAM%20Veracruz',
                        '/icons/phone.png',
                        '(229) 9891100',
                        true,
                    )}
                    <img class="up-toolbar-logo" src="/img/branding/logo_navbar.png" alt="CLAM Veracruz" />
                    ${this._upItem(
                        'https://www.facebook.com/clamveracruz/',
                        '/icons/facebook.png',
                        'clamveracruz',
                        true,
                    )}
                    ${this._upItem(
                        'https://twitter.com/ClamVeracruz',
                        '/icons/twitter.png',
                        html`<span>@</span>ClamVeracruz`,
                        true,
                    )}
                </div>

                <div id="main-toolbar">
                    <a class="nav-title" href="/" @click=${(e: Event) => this._navClick(e, 'nosotros')}>
                        <img src="/img/branding/logo_white.png" alt="CLAM" />
                        <p>Comité Local de Ayuda Mutua Región Veracruz</p>
                    </a>
                    <nav class="desktop-nav">
                        ${this._group('Inicio', 'ini', [
                            ['Nosotros', 'nosotros'],
                            ['Organigrama', 'organigrama'],
                        ])}
                        ${this._group('Subcomités', 'sub', [
                            ['Médico', 'medico'],
                            ['Medio Ambiente', 'ambiental'],
                            ['Seguridad', 'seguridad'],
                            ['Capacitación', 'capacitacion'],
                        ])}
                        ${this._group('Asociados', 'aso', [
                            ['Conócelos', 'conocelos'],
                            ['Registro', 'registro'],
                        ])}
                        ${this._group('Novedades', 'nov', [
                            ['Noticias', 'noticias'],
                            ['Eventos', 'eventos'],
                        ])}
                        ${this._navItem('Vinculación', 'vinculacion')}
                        <a class="plataforma-clam-btn" href="/login">Socio CLAM</a>
                    </nav>
                    ${this._mobile
                        ? html`<button class="menu-btn" @click=${() => (this._drawerOpen = true)}>☰</button>`
                        : nothing}
                </div>
            </header>

            <div class="header-spacer"></div>
            <main role="main">${this._renderPage()}</main>

            <div class="informative-content">
                <div class="address">
                    <h1>CLAM Veracruz</h1>
                    <p>Para mayor información contáctanos a través de..</p>
                    <p><span>Email: </span>clamveracruz@hotmail.com</p>
                    <p><span>Teléfono: </span>(229) 9891100</p>
                    <p>
                        <span>Dirección: </span>Carr. México – Veracruz, Km 433.7 Vía Xalapa Veracruz, Veracruz CP.
                        91697.
                    </p>
                </div>
                <div id="map"></div>
            </div>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'clam-main-app': ClamMainApp;
    }
}
